import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { OrgResource, readOrgResources } from "services/config-assignments.service";
import { FreeTime, readFreeTimes } from "services/facilities.service";

type Props = {
  orgId: number;
  /** A free time was chosen; the calendar opens its booking dialog for it. */
  onPick: (start: Date, end: Date) => void;
};

const todayLocal = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const timeLabel = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * The free times on one facility for one day: a cremator or chapel's open slots, with
 * closures and existing bookings already taken out by Core. Picking one starts a booking.
 */
const FreeTimesPanel = ({ orgId, onPick }: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [facilities, setFacilities] = React.useState<OrgResource[]>([]);
  const [resourceId, setResourceId] = React.useState("");
  const [day, setDay] = React.useState(todayLocal());
  const [times, setTimes] = React.useState<FreeTime[]>([]);
  const [state, setState] = React.useState<"idle" | "loading" | "error">("idle");
  const [message, setMessage] = React.useState("");

  React.useEffect(() => {
    if (orgId <= 0) return;
    let cancelled = false;
    (async () => {
      const token = await getAccessTokenSilently();
      const { data } = await readOrgResources(token, orgId);
      if (!cancelled) setFacilities(data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, getAccessTokenSilently]);

  React.useEffect(() => {
    if (!resourceId || !day) {
      setTimes([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setState("loading");
      const from = new Date(`${day}T00:00:00`);
      const to = new Date(from.getTime() + 24 * 60 * 60 * 1000);
      const token = await getAccessTokenSilently();
      const { data, error } = await readFreeTimes(token, orgId, Number(resourceId), from, to);
      if (cancelled) return;
      if (error) {
        setState("error");
        setMessage(error.message);
        setTimes([]);
        return;
      }
      setState("idle");
      setTimes(data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [resourceId, day, orgId, getAccessTokenSilently]);

  if (orgId <= 0) return null;

  return (
    <section className="border-b border-slate-100 px-4 py-3">
      <h2 className="text-sm font-semibold tracking-tight text-slate-900">Free times</h2>
      <p className="mt-0.5 mb-3 text-xs text-slate-500">Open slots on a facility for one day.</p>
      <div className="flex flex-col gap-2">
        <select
          aria-label="Facility"
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          value={resourceId}
          onChange={(e) => setResourceId(e.target.value)}
        >
          <option value="">Choose a facility…</option>
          {facilities.map((f) => (
            <option key={f.resourceId} value={f.resourceId}>
              {f.resourceName}
            </option>
          ))}
        </select>
        <input
          aria-label="Day"
          type="date"
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          value={day}
          onChange={(e) => setDay(e.target.value)}
        />
      </div>
      <div className="mt-3">
        {!resourceId && facilities.length === 0 && (
          <p className="text-xs text-slate-500">Add a facility under Facilities first.</p>
        )}
        {state === "loading" && <p className="text-xs text-slate-500">Finding free times…</p>}
        {state === "error" && <p className="text-xs text-red-500">{message}</p>}
        {state === "idle" && resourceId && times.length === 0 && (
          <p className="text-xs text-slate-500">No free times on this day.</p>
        )}
        {times.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {times.map((t) => (
              <li key={`${t.resourceId}-${t.start}`}>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  onClick={() => onPick(new Date(t.start), new Date(t.end))}
                >
                  {timeLabel(t.start)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default FreeTimesPanel;
